import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus,
  Loader,
  FilePlus,
  Edit,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { toast } from 'react-toastify';
import BidCreateModal from '../components/BidCreateModal';
import BidEditModal from '../components/BidEditModal';
import VoiceInput from '../components/VoiceInput';
import { AuthContext } from '../../Auth/context/AuthContext';

// Utility: escape regex special characters
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// Component to highlight matching terms in text
const Highlighter = ({ text, highlights }) => {
  if (!highlights || highlights.length === 0 || !text) return <>{text}</>;
  const regex = new RegExp(`(${highlights.map(escapeRegExp).join("|")})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part
      )}
    </>
  );
};
const BidsListPage = () => {
  /* --------------------------------------------------
   * STATE
   * ------------------------------------------------*/
  const STATUSES = ['open', 'won', 'lost', 'archived'];

  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const limit = 10;

  // sorting
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBid, setEditingBid] = useState(null);

  // search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  // live terms for highlighting
  const terms = search.trim().split(/\s+/).filter(Boolean);
  // keyword cloud
  const [keywords, setKeywords] = useState([]);

  /* --------------------------------------------------
   * EFFECTS
   * ------------------------------------------------*/
  // debounce search input
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);

    return () => clearTimeout(id);
  }, [search]);

  // reset to first page when sorting changes
  useEffect(() => {
    setPage(1);
  }, [sortField, sortOrder]);

  /* --------------------------------------------------
   * FETCH DATA
   * ------------------------------------------------*/
  const { auth } = useContext(AuthContext);
  const authToken = localStorage.getItem('authToken');
  const isAdmin = auth.user_type === 'Organization';
  const navigate = useNavigate();

  const fetchBids = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const resp = await axios.get('/api/myteconstruction/bids', {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          page,
          limit,
          sort: sortField,
          order: sortOrder,
          q: debouncedSearch,
        },
      });

      setBids(resp.data.items);
      setPages(resp.data.pages);
      setKeywords(resp.data.keywords || []);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || 'Failed to load bids';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [authToken, page, limit, sortField, sortOrder, debouncedSearch]);

  useEffect(() => {
    fetchBids();
  }, [fetchBids]);

  /* --------------------------------------------------
   * HELPERS
   * ------------------------------------------------*/
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleCreate = (newBid) => setBids((prev) => [...prev, newBid]);

  const handleUpdate = (updatedBid) => {
    setBids((prev) => prev.map((b) => (b._id === updatedBid._id ? updatedBid : b)));
    setEditingBid(null);
  };

  const colorFor = (status) =>
    (
      {
        open: 'bg-blue-600',
        won: 'bg-green-600',
        lost: 'bg-red-600',
        archived: 'bg-gray-500',
      }[status] || 'bg-gray-600'
    );

  /* --------------------------------------------------
   * RENDER
   * ------------------------------------------------*/
  return (
    <div className="p-4">
      {/* header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Bids</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-1" />
          <span>New Bid</span>
        </button>
      </div>

      {/* search input always visible for fluid experience */}
      <div className="mb-4 flex items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by any field…"
          className="flex-grow p-2 rounded bg-gray-200 text-gray-900"
        />
        <VoiceInput onResult={setSearch} />
      </div>
      {/* keyword tag-cloud */}
      {keywords.length > 0 && (
        <div className="mb-2 flex space-x-2 overflow-x-auto">
          {keywords.map((k) => (
            <button
              key={k.term}
              className="flex-none bg-gray-200 text-gray-800 px-2 py-1 rounded hover:bg-gray-300"
              onClick={() => {
                if (!terms.includes(k.term)) {
                  setSearch((prev) => (prev ? prev + ' ' + k.term : k.term));
                }
              }}
            >
              {k.term} ({k.count})
            </button>
          ))}
        </div>
      )}
      {/* active filter chips */}
      {terms.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {terms.map((t) => (
            <div
              key={t}
              className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded"
            >
              <span>{t}</span>
              <button
                type="button"
                className="ml-1 text-blue-600 hover:text-blue-800"
                onClick={() => {
                  const newTerms = terms.filter((x) => x !== t);
                  setSearch(newTerms.join(' '));
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* loading / error */}
      {loading && (
        <div className="flex justify-center py-6">
          <Loader className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      )}
      {error && (
        <p className="text-red-500 mb-4">{error}</p>
      )}

      {/* table / empty state */}
      {!loading && !error && (
        bids.length === 0 ? (
          <div className="text-gray-400 p-4 text-center">No bids found.</div>
        ) : (
          <>
            <table className="w-full bg-gray-800 text-white rounded-lg overflow-hidden">
              <thead className="bg-gray-700">
                <tr>
                  {/* name */}
                  <th className="text-left p-3 cursor-pointer" onClick={() => handleSort('name')}>
                    <div className="flex items-center space-x-1">
                      <span>Name</span>
                      {sortField === 'name' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ChevronsUpDown size={14} />
                      )}
                    </div>
                  </th>
                  {/* client */}
                  <th className="text-left p-3 cursor-pointer" onClick={() => handleSort('client')}>
                    <div className="flex items-center space-x-1">
                      <span>Client</span>
                      {sortField === 'client' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ChevronsUpDown size={14} />
                      )}
                    </div>
                  </th>
                  {/* due date */}
                  <th className="text-left p-3 cursor-pointer" onClick={() => handleSort('dueDate')}>
                    <div className="flex items-center space-x-1">
                      <span>Due Date</span>
                      {sortField === 'dueDate' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ChevronsUpDown size={14} />
                      )}
                    </div>
                  </th>
                  {/* status */}
                  <th className="text-left p-3 cursor-pointer" onClick={() => handleSort('status')}>
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortField === 'status' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ChevronsUpDown size={14} />
                      )}
                    </div>
                  </th>
                  {isAdmin && <th className="text-left p-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {bids.map((bid) => (
                <tr
                  key={bid._id}
                  onClick={() => navigate(bid._id)}
                  className="cursor-pointer border-b border-gray-700 hover:bg-gray-700 transition-colors"
                >
                <td className="p-3 flex items-center gap-2">
                      <FilePlus className="h-4 w-4 text-blue-400" />
                      <Highlighter text={bid.name} highlights={terms} />
                    </td>
                    <td className="p-3">
                      {bid.client ? (
                        <Highlighter text={bid.client} highlights={terms} />
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="p-3">
                      {bid.dueDate ? new Date(bid.dueDate).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3">
                      {isAdmin ? (
                        <select
                          className="px-2 py-1 rounded bg-gray-800 text-white"
                          value={bid.status}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await axios.patch(
                                `/api/myteconstruction/bids/${bid._id}/status`,
                                { status: newStatus },
                                { headers: { Authorization: `Bearer ${authToken}` } }
                              );
                              setBids((prev) =>
                                prev.map((b) =>
                                  b._id === bid._id ? { ...b, status: newStatus } : b
                                )
                              );
                              toast.success('Status updated');
                            } catch (err) {
                              const msg = err.response?.data?.error || 'Failed to update status';
                              toast.error(msg);
                            }
                          }}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`px-2 py-1 rounded capitalize ${colorFor(bid.status)}`}>
                          <Highlighter text={bid.status} highlights={terms} />
                        </span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="p-3 flex items-center space-x-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingBid(bid); }}
                          className="hover:text-gray-300"
                          aria-label="Edit bid"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm('Are you sure you want to delete this bid?')) return;
                            try {
                              await axios.delete(`/api/myteconstruction/bids/${bid._id}`, {
                                headers: { Authorization: `Bearer ${authToken}` },
                              });
                              setBids((prev) => prev.filter((b) => b._id !== bid._id));
                              toast.success('Bid deleted successfully');
                            } catch (err) {
                              const msg = err.response?.data?.error || 'Failed to delete bid';
                              toast.error(msg);
                            }
                          }}
                          className="hover:text-red-400"
                          aria-label="Delete bid"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* pagination */}
            <div className="flex justify-center items-center mt-4 space-x-4">
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page <= 1}
                className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span>
                Page {page} of {pages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(p + 1, pages))}
                disabled={page >= pages}
                className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )
      )}

      {/* modals */}
      {showCreateModal && (
        <BidCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(bid) => {
            handleCreate(bid);
            setShowCreateModal(false);
          }}
        />
      )}
      {editingBid && (
        <BidEditModal
          bid={editingBid}
          onClose={() => setEditingBid(null)}
          onSave={handleUpdate}
        />
      )}
    </div>
  );
};

export default BidsListPage;
